/* import __COLOCATED_TEMPLATE__ from './shopify.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type { TaskGenerator } from 'ember-concurrency';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import ajax from 'embercom/lib/ajax';

interface AuditEvent {
  id: number;
  event_type: string;
  created_at: string;
  admin_name: string;
  admin_email: string | null;
  order_id: string | null;
  order_name: string | null;
  shopify_domain: string | null;
  compensation_type: string | null;
}

interface FormattedAuditEvent extends AuditEvent {
  formattedDate: string;
}

export default class ShopifyAuditingEvents extends Component {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intl: IntlService;

  @tracked events: AuditEvent[] = [];

  get formattedEvents(): FormattedAuditEvent[] {
    return this.events.map((e) => ({
      ...e,
      formattedDate: new Date(e.created_at).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    }));
  }
  @tracked fromDate: Date = this.defaultFromDate;
  @tracked toDate: Date = new Date();

  constructor(owner: unknown, args: {}) {
    super(owner, args);
    taskFor(this.fetchEvents).perform();
  }

  get defaultFromDate(): Date {
    let d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }

  get fromTimestamp(): number {
    return Math.floor(this.fromDate.getTime() / 1000);
  }

  get toTimestamp(): number {
    return Math.floor(this.toDate.getTime() / 1000);
  }

  get today(): Date {
    return new Date();
  }

  get isLoading(): boolean {
    return taskFor(this.fetchEvents).isRunning;
  }

  @dropTask
  *fetchEvents(): TaskGenerator<void> {
    try {
      let response: { events: AuditEvent[] } = yield ajax({
        url: '/ember/shopify/integrations/auditing_events',
        type: 'GET',
        data: {
          app_id: this.appService.app.id,
          from: this.fromTimestamp,
          to: this.toTimestamp,
        },
      });
      this.events = response.events ?? [];
    } catch (error: any) {
      let errorKey = error?.jqXHR?.responseJSON?.error_key ?? 'load-failure';
      this.notificationsService.notifyError(
        this.intl.t(`appstore.settings.shopify.auditing-events.${errorKey}`),
      );
      this.events = [];
    }
  }

  @action
  onFromDateChange(date: Date): void {
    this.fromDate = date;
    taskFor(this.fetchEvents).perform();
  }

  @action
  onToDateChange(date: Date): void {
    this.toDate = date;
    taskFor(this.fetchEvents).perform();
  }

  @action
  async exportCsv(): Promise<void> {
    let params = new URLSearchParams({
      app_id: String(this.appService.app.id),
      from: String(this.fromTimestamp),
      to: String(this.toTimestamp),
    });
    try {
      let response = await fetch(`/ember/shopify/integrations/auditing_events_csv?${params}`);
      if (!response.ok) {
        let json = await response.json();
        let errorKey = json?.error_key ?? 'load-failure';
        this.notificationsService.notifyError(
          this.intl.t(`appstore.settings.shopify.auditing-events.${errorKey}`),
        );
        return;
      }
      let blob = await response.blob();
      // eslint-disable-next-line @intercom/intercom/no-bare-strings -- HTTP header name
      let disposition = response.headers.get('Content-Disposition') ?? '';
      let filename = disposition.match(/filename="(.+)"/)?.[1] ?? 'shopify_audit_events.csv';
      let url = window.URL.createObjectURL(blob);
      let a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('appstore.settings.shopify.auditing-events.load-failure'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Appstore::AppPackage::Auditing::Shopify': typeof ShopifyAuditingEvents;
    'appstore/app-package/auditing/shopify': typeof ShopifyAuditingEvents;
  }
}
